import { useEffect, useRef } from "react";

import { STATUS_POLL_INTERVALS_MS } from "../constants/verification.constants";
import type { VerificationStatusResponse } from "../types/verification-api.types";

const TERMINAL_STATUSES = new Set(["VERIFIED", "REJECTED", "EXPIRED"]);

/**
 * Polls a status endpoint with backoff while `enabled` is true, calling `onUpdate` on each tick and stopping
 * on a terminal status. Frame-agnostic: `fetchStatus` wraps the public (identity token) or internal (JWT)
 * call. This is the fallback path; the preferred channel is the `verification:updated` push on the
 * notifications socket (see `useVerificationSocket`), and the two coexist.
 */
export function useVerificationStatusPolling(
  fetchStatus: () => Promise<VerificationStatusResponse>,
  enabled: boolean,
  onUpdate: (status: VerificationStatusResponse) => void,
): void {
  const fetchStatusRef = useRef(fetchStatus);
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    fetchStatusRef.current = fetchStatus;
    onUpdateRef.current = onUpdate;
  });

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const data = await fetchStatusRef.current();
        if (cancelled) return;
        onUpdateRef.current(data);
        if (TERMINAL_STATUSES.has(data.status)) return;
      } catch {
        // Transient error — keep polling with backoff.
      }
      if (cancelled) return;
      const delay = STATUS_POLL_INTERVALS_MS[Math.min(attempt, STATUS_POLL_INTERVALS_MS.length - 1)];
      attempt += 1;
      timer = setTimeout(() => void poll(), delay);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);
}
